import { useState, useEffect } from 'react';
import api from '../../api';

const cardStyle = {
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: '16px', padding: '20px 24px', boxShadow: 'var(--shadow-card)',
};

const inputStyle = {
  width: '100%', padding: '10px 12px', boxSizing: 'border-box',
  border: '2px solid var(--border)', borderRadius: '10px',
  fontSize: '14px', fontFamily: 'var(--font-body)',
  background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none',
};

function DeptForm({ initial, ranks, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    name: '', color: '#5865F2', hourly_rate_override: '', rank_ids: [],
  });

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleRank = (id) => setForm(p => ({
    ...p,
    rank_ids: p.rank_ids.includes(id)
      ? p.rank_ids.filter(r => r !== id)
      : [...p.rank_ids, id],
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px' }}>
        <div>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Department Name</label>
          <input type="text" value={form.name} onChange={e => update('name', e.target.value)}
            placeholder="e.g. Medical, Military, Research" style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Color</label>
          <input type="color" value={form.color} onChange={e => update('color', e.target.value)}
            style={{ width: '48px', height: '42px', border: '2px solid var(--border)', borderRadius: '10px', cursor: 'pointer', background: 'none', padding: '2px' }} />
        </div>
      </div>
      <div>
        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Hourly Rate Override (optional)</label>
        <input type="number" min="0" step="0.01" value={form.hourly_rate_override}
          onChange={e => update('hourly_rate_override', e.target.value)}
          placeholder="Leave blank to use rank rate" style={inputStyle} />
      </div>
      <div>
        <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>Lock Ranks to this Department</label>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px' }}>Locked ranks are exclusive to this department. Unlocked ranks remain global.</div>
        {ranks.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No ranks created yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto' }}>
            {ranks.map(r => (
              <label key={r.id} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '9px 12px', borderRadius: '8px', cursor: 'pointer',
                background: form.rank_ids.includes(r.id) ? 'var(--accent-soft)' : 'var(--bg-secondary)',
                border: `1px solid ${form.rank_ids.includes(r.id) ? 'var(--accent-primary)' : 'var(--border)'}`,
              }}>
                <input type="checkbox" checked={form.rank_ids.includes(r.id)}
                  onChange={() => toggleRank(r.id)} />
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{r.name}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Level {r.level}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={() => onSave(form)} style={{
          flex: 1, padding: '12px', background: 'var(--gradient-accent)', color: '#fff',
          border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700,
          cursor: 'pointer', fontFamily: 'var(--font-display)',
        }}>Save Department</button>
        <button onClick={onCancel} style={{
          padding: '12px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)',
          borderRadius: '10px', fontSize: '14px', cursor: 'pointer', color: 'var(--text-secondary)',
        }}>Cancel</button>
      </div>
    </div>
  );
}

export default function DepartmentsPage({ guild }) {
  const [departments, setDepartments] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (!guild || !guild.id) return;
    setLoading(true);
    setError(null);
    Promise.all([
      api.get(`/api/departments/${guild.id}`),
      api.get(`/api/guilds/${guild.id}/ranks`),
    ])
      .then(([dRes, rRes]) => {
        setDepartments(dRes.data);
        setRanks(rRes.data);
      })
      .catch(err => {
        console.error('DepartmentsPage load error:', err);
        setError(err?.response?.data?.error || err?.message || 'Failed to load departments');
      })
      .finally(() => setLoading(false));
  }, [guild && guild.id]);

  const handleSave = async (form) => {
    const payload = {
      name: form.name,
      color: form.color,
      hourly_rate_override: form.hourly_rate_override ? parseFloat(form.hourly_rate_override) : null,
      rank_ids: form.rank_ids,
    };
    try {
      if (editing) {
        await api.patch(`/api/departments/${guild.id}/${editing.id}`, payload);
      } else {
        await api.post(`/api/departments/${guild.id}`, payload);
      }
      setCreating(false);
      setEditing(null);
      setLoading(true);
      const [dRes, rRes] = await Promise.all([
        api.get(`/api/departments/${guild.id}`),
        api.get(`/api/guilds/${guild.id}/ranks`),
      ]);
      setDepartments(dRes.data);
      setRanks(rRes.data);
    } catch (err) {
      alert('Failed to save department: ' + (err?.response?.data?.error || err?.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this department? Staff will be unassigned and their shifts in this department will be untagged.')) return;
    setDeleting(id);
    try {
      await api.delete(`/api/departments/${guild.id}/${id}`);
      setDepartments(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      alert('Failed to delete department.');
    } finally {
      setDeleting(null);
    }
  };

  if (!guild) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>⏳</div>
        Loading server data...
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-muted)' }}>
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>🏢</div>
        Loading departments...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '60px' }}>
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div>
        <div style={{ color: 'var(--danger)', fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>Failed to load departments</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '32px', color: 'var(--text-primary)', marginBottom: '6px' }}>Departments</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '15px' }}>
            {departments.length} department{departments.length !== 1 ? 's' : ''} · Organize staff into groups with their own ranks and pay rates
          </p>
        </div>
        {!creating && !editing && (
          <button onClick={() => setCreating(true)} style={{
            padding: '12px 24px', background: 'var(--gradient-accent)', color: '#fff',
            border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 700,
            cursor: 'pointer', fontFamily: 'var(--font-display)', boxShadow: 'var(--shadow)',
          }}>+ New Department</button>
        )}
      </div>

      {creating && (
        <div style={{ ...cardStyle, marginBottom: '24px', border: '2px solid var(--accent-primary)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--text-primary)', marginBottom: '20px' }}>New Department</h3>
          <DeptForm ranks={ranks} onSave={handleSave} onCancel={() => setCreating(false)} />
        </div>
      )}

      {editing && (
        <div style={{ ...cardStyle, marginBottom: '24px', border: '2px solid var(--accent-primary)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', color: 'var(--text-primary)', marginBottom: '20px' }}>Edit Department</h3>
          <DeptForm
            initial={{ ...editing, rank_ids: (editing.locked_ranks || []).map(r => r.id), hourly_rate_override: editing.hourly_rate_override || '' }}
            ranks={ranks}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {departments.length === 0 && !creating && !editing ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '80px' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🏢</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>No departments yet</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '15px', marginBottom: '24px' }}>
            Create departments to organize your staff into groups with their own ranks and pay rates.
          </div>
          <button onClick={() => setCreating(true)} style={{
            padding: '12px 28px', background: 'var(--gradient-accent)', color: '#fff',
            border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 700,
            cursor: 'pointer', fontFamily: 'var(--font-display)',
          }}>Create First Department</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {departments.map(dept => (
            <div key={dept.id} style={{ ...cardStyle, borderTop: `4px solid ${dept.color}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: dept.color, flexShrink: 0 }} />
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>{dept.name}</div>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => { setEditing(dept); setCreating(false); }}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}>✏️</button>
                  <button onClick={() => handleDelete(dept.id)} disabled={deleting === dept.id}
                    style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--danger)', background: 'transparent', color: 'var(--danger)', cursor: 'pointer', fontSize: '12px' }}>
                    {deleting === dept.id ? '...' : '🗑️'}
                  </button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'Staff', value: dept.staff_count || 0 },
                  { label: 'Period Hrs', value: parseFloat(dept.period_hours || 0).toFixed(1) },
                  { label: 'Owed', value: parseFloat(dept.owed_balance || 0).toFixed(2) },
                ].map(stat => (
                  <div key={stat.label} style={{ background: 'var(--bg-secondary)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>{stat.value}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
              {dept.locked_ranks?.length > 0 && (
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', marginBottom: '6px' }}>Locked Ranks</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {dept.locked_ranks.map(r => (
                      <span key={r.id} style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '100px', background: dept.color + '20', color: dept.color, border: `1px solid ${dept.color}40`, fontWeight: 600 }}>{r.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {dept.hourly_rate_override && (
                <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-muted)' }}>
                  💰 Rate override: <strong style={{ color: 'var(--text-primary)' }}>{parseFloat(dept.hourly_rate_override).toFixed(2)}/hr</strong>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
